import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, Participant, SourceData, SourceDataReview, SourceDataVerification, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { createEvent } from "./useEvent";
import { ItemType } from "../configuration/useItemConfigurations";
import { createItem } from "./useItem";
import { createItemGroup } from "./useItemGroup";
import { AppSettings } from "@/AppSettings";

export interface Item {
    type: ItemType.Item;
    itemGroup?: NonNullable<ReturnType<typeof createItemGroup>>;
    item: NonNullable<ReturnType<typeof createItem>>;
    lastInGroup?: boolean;
    secondaryOrder: number;
    secondaryRepeat: number;
    primaryRepeat: number;
    primaryOrder: number;
}

export interface ItemGroup {
    type: ItemType.ItemGroup;
    itemGroup: NonNullable<ReturnType<typeof createItemGroup>>;
    secondaryOrder: number;
    secondaryRepeat: number;
    primaryRepeat: number;
    primaryOrder: number;
}

const useItemsVirtualized = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    sourceData?: SourceData,
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    preview?: boolean
) => {
    return useMemo(() => {
        if (!metadata || !metadata.events) {
            return undefined;
        }

        if (
            !studyEventOid ||
            !studyEventRepeat ||
            !caseReportFormOid ||
            !caseReportFormVersionOid ||
            !metadata || 
            (studyEventOid && !metadata.eventForms?.[studyEventOid]?.[caseReportFormOid]) ||
            !metadata.items?.[caseReportFormOid]?.[caseReportFormVersionOid]
        ) {
            return undefined;
        }

        //NonNullable<ReturnType<typeof createItemConfiguration>>
        return Object.keys(metadata.items[caseReportFormOid][caseReportFormVersionOid]).reduce<Array<Item | ItemGroup>>((items, igName) => {
            if (igName === AppSettings.metadata.itemGroupUngroupedKey) {
                const groupItems = Object.keys(metadata.items![caseReportFormOid][caseReportFormVersionOid][igName]).map((itemName) => {
                    const item = createItem(
                        metadata, 
                        trialConfiguration?.items, 
                        configuration?.items,
                        sourceData?.items,
                        trialConfiguration?.itemValues, 
                        configuration?.itemValues,
                        trial, 
                        masterGroup,
                        coordinatingGroup,
                        institution,
                        participant,
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id, 
                        studyEventOid,
                        studyEventRepeat,
                        caseReportFormOid,
                        caseReportFormVersionOid,
                        igName === AppSettings.metadata.itemGroupUngroupedKey ? undefined : igName,
                        1,
                        itemName,
                        preview
                    );

                    return {
                        type: ItemType.Item,
                        item: item,
                        secondaryOrder: 1,
                        secondaryRepeat: 1,
                        primaryRepeat: 1,
                        primaryOrder: item?.itemMetadata.order ?? 0
                    } as Item
                })
                .filter(x => !!x.item);

                return [...items, ...groupItems];
            }
            else {
                const sourceDataItemGroups = sourceData?.items?.[studyEventOid]?.[studyEventRepeat]?.[caseReportFormOid]?.[igName];

                const sourceDataItemGroupRepeats = Object.keys(sourceDataItemGroups ?? {});
                
                const groups: (ItemGroup | Item)[] = [];
                
                
                if (!sourceDataItemGroupRepeats || sourceDataItemGroupRepeats.length === 0) {
                    

                    const group = createItemGroup(
                        metadata, 
                        trialConfiguration?.itemGroups, 
                        configuration?.itemGroups, 
                        sourceData?.items,
                        trial, 
                        masterGroup,
                        coordinatingGroup,
                        institution,
                        participant,
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id, 
                        studyEventOid,
                        studyEventRepeat,
                        caseReportFormOid,
                        caseReportFormVersionOid,
                        igName,
                        1,
                        preview
                    );

                    if (group) {
                        const groupItems = Object.keys(metadata.items![caseReportFormOid][caseReportFormVersionOid][igName]).map((itemName) => {
                            const item = createItem(
                                metadata, 
                                trialConfiguration?.items, 
                                configuration?.items, 
                                sourceData?.items,
                                trialConfiguration?.itemValues, 
                                configuration?.itemValues,
                                trial, 
                                masterGroup,
                                coordinatingGroup,
                                institution,
                                participant,
                                configuration?.configuration?.isGlobal,
                                configuration?.configuration?.id,  
                                studyEventOid,
                                studyEventRepeat,
                                caseReportFormOid,
                                caseReportFormVersionOid,
                                igName,
                                1,
                                itemName,
                                preview
                            );
    
                            return {
                                type: ItemType.Item,
                                item: item,
                                itemGroup: group,
                                secondaryOrder: 1,
                                secondaryRepeat: 1,
                                primaryRepeat: 1,
                                primaryOrder: item?.itemMetadata.order ?? 0
                            } as Item
                        })
                        .filter(x => !!x.item)
                        .sort((a, b) => a.primaryOrder < b.primaryOrder ? -1 : 1)
    
                        const groupOrder = groupItems.reduce<number | undefined>((min, current) => {
                            if (!min || min > current.primaryOrder) {
                                return current.primaryOrder;
                            }

                            return min;
                        }, undefined) ?? 0;

                        groups.push(
                            {
                                type: ItemType.ItemGroup,
                                itemGroup: group,
                                secondaryOrder: 0,
                                secondaryRepeat: 0,
                                primaryRepeat: 1,
                                primaryOrder: groupOrder
                            } as ItemGroup
                        );

                        let itemCount = 0;

                        for (let groupItem of groupItems) {
                            itemCount++;

                            groupItem.secondaryOrder = groupItem.primaryOrder;
                            groupItem.secondaryRepeat = groupItem.secondaryRepeat;
                            groupItem.primaryOrder = groupOrder;
                            groupItem.primaryRepeat = 1;
                                
                            if (itemCount === groupItems.length) {
                                groupItem.lastInGroup = true;
                            }

                            groups.push(groupItem);
                        }
                    }
                }
                else {

                    for (let itemGroupRepeat of sourceDataItemGroupRepeats) {
                        
                        const group = createItemGroup(
                            metadata, 
                            trialConfiguration?.itemGroups, 
                            configuration?.itemGroups, 
                            sourceData?.items,
                            trial, 
                            masterGroup,
                            coordinatingGroup,
                            institution,
                            participant,
                            configuration?.configuration?.isGlobal,
                            configuration?.configuration?.id, 
                            studyEventOid,
                            studyEventRepeat,
                            caseReportFormOid,
                            caseReportFormVersionOid,
                            igName,
                            +itemGroupRepeat,
                            preview
                        );
                            
                        if (group) {
                            const groupItems = Object.keys(metadata.items![caseReportFormOid][caseReportFormVersionOid][igName]).map((itemName) => {
                                const item = createItem(
                                    metadata, 
                                    trialConfiguration?.items, 
                                    configuration?.items, 
                                    sourceData?.items,
                                    trialConfiguration?.itemValues, 
                                    configuration?.itemValues,
                                    trial, 
                                    masterGroup,
                                    coordinatingGroup,
                                    institution,
                                    participant,
                                    configuration?.configuration?.isGlobal,
                                    configuration?.configuration?.id,  
                                    studyEventOid,
                                    studyEventRepeat,
                                    caseReportFormOid,
                                    caseReportFormVersionOid,
                                    igName,
                                    +itemGroupRepeat,
                                    itemName,
                                    preview
                                );
            
                                return {
                                    type: ItemType.Item,
                                    item: item,
                                    itemGroup: group,
                                    repeat: +itemGroupRepeat,
                                    secondaryOrder: 1,
                                    secondaryRepeat: 1,
                                    primaryRepeat: 1,
                                    primaryOrder: item?.itemMetadata.order ?? 0
                                } as Item
                            })
                            .filter(x => !!x.item)
                            .sort((a, b) => a.primaryOrder < b.primaryOrder ? -1 : 1)
                            
                            const groupOrder = groupItems.reduce<number | undefined>((min, current) => {
                                if (!min || min > current.primaryOrder) {
                                    return current.primaryOrder;
                                }
    
                                return min;
                            }, undefined) ?? 0;

                            groups.push(
                                {
                                    type: ItemType.ItemGroup,
                                    itemGroup: group,
                                    secondaryOrder: 0,
                                    secondaryRepeat: 0,
                                    primaryRepeat: +itemGroupRepeat,
                                    primaryOrder: groupOrder
                                } as ItemGroup
                            );

                            let itemCount = 0;
                            
                            for (let groupItem of groupItems) {
                                itemCount++;

                                groupItem.secondaryOrder = groupItem.primaryOrder;
                                groupItem.secondaryRepeat = groupItem.secondaryRepeat;
                                groupItem.primaryOrder = groupOrder;
                                groupItem.primaryRepeat = +itemGroupRepeat;
                                
                                if (itemCount === groupItems.length) {
                                    groupItem.lastInGroup = true;
                                }

                                groups.push(groupItem);
                            }
                        }
                    }
                }

                return [ 
                    ...items,
                    ...groups
                ]
            }
        }, [])
        .filter(x => !!x)
        .sort((a, b) => {
            if (a.primaryOrder === b.primaryOrder) {
                if (a.primaryRepeat === b.primaryRepeat) {
                    if (a.secondaryOrder === b.secondaryOrder) {
                        return a.secondaryRepeat < b.secondaryRepeat ? -1 : 1 
                    }
                        
                    return a.secondaryOrder < b.secondaryOrder ? -1 : 1 
                }

                return a.primaryRepeat < b.primaryRepeat ? -1 : 1 
            }
                
            return a.primaryOrder < b.primaryOrder ? -1 : 1 
        })
    }, [
        metadata, 
        trialConfiguration?.items, 
        configuration?.items, 
        trialConfiguration?.itemGroups, 
        configuration?.itemGroups, 
        sourceData?.items, 
        trialConfiguration?.itemValues, 
        configuration?.itemValues,
        trial, 
        institution,
        masterGroup, 
        coordinatingGroup, 
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        preview
    ]) as Array<Item | ItemGroup> | undefined
};

export default useItemsVirtualized;